import { useMemo, useRef, useState } from "react";
import {
  createFileRoute,
  stripSearchParams,
  Link,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  BookOpen,
  Sparkles,
  TrendingUp,
  MessageSquare,
  Info,
} from "lucide-react";
import type {
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";

import {
  useRecommendations,
  useCellRecommendationCountsByTenant,
} from "@/hooks/use-kb-insights";
import type { Recommendation } from "@/hooks/use-kb-insights";
import { useCellScope, type CellScope } from "@/hooks/use-cell-scope";
import { CellSelect } from "@/components/cells/cell-select";
import { CellScopeAlerts } from "@/components/cells/cell-scope-alerts";
import { CELLS, findCell, tenantLabel } from "@/lib/cells/cells";
import { createReadOnlyColumns } from "@/components/kb-insights/read-only-columns";
import {
  DataTable,
  DataTableToolbar,
  useDataTable,
  type DataTableToolbarRef,
} from "@/components/common/data-table";
import { Page, PageHeader, PageTitle } from "@/components/common/page";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const CLASSIFICATION_TOOLTIPS = new Map([
  [
    "partially_deflectable",
    "In some cases there were opportunities for deflection, in others there weren't",
  ],
  [
    "potentially_deflectable",
    "No evidence it would lead to deflections, but there is evidence the article arose from conversation deflection attempts",
  ],
]);

const DEFLECTABLE_OPTIONS = [
  { label: "Completely Deflectable", value: "deflectable" },
  { label: "Partially Deflectable", value: "partially_deflectable" },
  { label: "Potentially Deflectable", value: "potentially_deflectable" },
] as const;

const SELF_SERVICEABLE_OPTIONS = [
  ...DEFLECTABLE_OPTIONS,
  { label: "Not Deflectable", value: "not_deflectable" },
] as const;

const AUTOMATABLE_OPTIONS = [
  { label: "Automatable", value: "automatable" },
  { label: "Automatable with Approval", value: "automatable_with_approval" },
] as const;

const ALL_SELF_SERVICEABLE_VALUES: string[] = SELF_SERVICEABLE_OPTIONS.map(
  (o) => o.value,
);

const ALL_KIND_VALUES: string[] = [
  "deflectable",
  ...AUTOMATABLE_OPTIONS.map((o) => o.value),
  "structured_escalation",
];

// Largest page_size the insights-engine list endpoint accepts (Query le=200).
const MAX_PAGE_SIZE = 200;

const CLASSIFICATION_RANK = new Map([
  ["deflectable", 5],
  ["potentially_deflectable", 4],
  ["partially_deflectable", 3],
  ["automatable", 3],
  ["automatable_with_approval", 2],
  ["not_deflectable", 1],
  ["structured_escalation", 1],
  ["junk", 0],
]);

const defaultValues = {
  tenant_id: "",
  cell: "all",
  status: [] as string[],
  self_serviceable: ALL_SELF_SERVICEABLE_VALUES,
  kind: ALL_KIND_VALUES,
  highQuality: true,
  belowThreshold: false,
  minSources: 0,
  maxSources: 21,
  q: "",
  page: 0,
  pageSize: 50,
  sortBy: "conversation_count" as const,
  sortDirection: "desc" as "desc" | "asc",
};

const searchSchema = z.object({
  tenant_id: z.string().default(defaultValues.tenant_id),
  cell: z.string().default(defaultValues.cell),
  status: z.array(z.string()).default(defaultValues.status),
  self_serviceable: z.array(z.string()).default(defaultValues.self_serviceable),
  kind: z.array(z.string()).default(defaultValues.kind),
  highQuality: z.boolean().default(defaultValues.highQuality),
  belowThreshold: z.boolean().default(defaultValues.belowThreshold),
  minSources: z.number().min(0).default(defaultValues.minSources),
  maxSources: z.number().min(0).default(defaultValues.maxSources),
  q: z.string().default(defaultValues.q),
  page: z.number().min(0).default(defaultValues.page),
  pageSize: z.number().min(10).max(200).default(defaultValues.pageSize),
  sortBy: z.string().default(defaultValues.sortBy),
  sortDirection: z.enum(["desc", "asc"]).default(defaultValues.sortDirection),
});

type SearchSchema = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/agent/kb-insights/")({
  component: KBInsightsPage,
  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams(defaultValues)] },
});

function getClassificationScore(value: string | boolean | null): number {
  if (value === true) return 4;
  if (typeof value === "string") return CLASSIFICATION_RANK.get(value) ?? 0;
  return 0;
}

function KBInsightsPage() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const toolbarRef = useRef<DataTableToolbarRef | null>(null);
  const [tenantOpen, setTenantOpen] = useState(false);

  const cellFilter =
    CELLS.length > 1 ? (findCell(searchParams.cell)?.id ?? "all") : "all";
  const scope = useCellScope({
    cellFilter,
    tenantId: searchParams.tenant_id || undefined,
  });
  const { tenants, selectedTenant } = scope;
  // The picker offers the chosen cell's tenants.
  const sortedTenants = useMemo(
    () =>
      tenants
        ?.filter(
          (t) => cellFilter === "all" || t.deployment_cell === cellFilter,
        )
        .sort((a, b) => a.company.localeCompare(b.company)) ?? [],
    [tenants, cellFilter],
  );
  const tenantCell = findCell(selectedTenant?.deployment_cell);
  const handleCellChange = (value: string) => {
    const keepTenant = selectedTenant?.deployment_cell === value;
    updateSearch({
      cell: value,
      page: 0,
      ...(keepTenant ? {} : { tenant_id: "" }),
    });
  };

  const hasTenant = searchParams.tenant_id !== "";

  // Quality scope. At least one view is always on; with both on the server
  // applies no quality filter and the list is ordered purely by the sort.
  const showBelow = searchParams.belowThreshold;
  const showHigh = searchParams.highQuality || !showBelow;
  const qualityFilter: boolean | undefined =
    showHigh && showBelow ? undefined : showHigh;

  // Values from older bookmarks that are no longer offered are ignored.
  const selectedKinds = useMemo(
    () => searchParams.kind.filter((k) => ALL_KIND_VALUES.includes(k)),
    [searchParams.kind],
  );

  const {
    data: response,
    isLoading,
    isRefetching,
    refetch,
    error: recommendationsError,
  } = useRecommendations({
    enabled: hasTenant,
    tenant_id: searchParams.tenant_id,
    status: searchParams.status.length > 0 ? searchParams.status : undefined,
    // Applied server-side so the 200-row cap is taken from the filtered,
    // sorted set rather than trimming quality rows out of an arbitrary page.
    high_quality: qualityFilter,
    search: searchParams.q || undefined,
    min_conversations:
      searchParams.minSources > 0 ? searchParams.minSources : undefined,
    max_conversations:
      searchParams.maxSources < 21 ? searchParams.maxSources : undefined,
    page: 0,
    page_size: MAX_PAGE_SIZE,
    sort_by: searchParams.sortBy,
    sort_order: searchParams.sortDirection,
  });

  const { data: allForTopFindings } = useRecommendations({
    enabled: hasTenant,
    tenant_id: searchParams.tenant_id,
    high_quality: qualityFilter,
    page: 0,
    page_size: MAX_PAGE_SIZE,
    sort_by: "conversation_count",
    sort_order: "desc",
  });

  const allRecommendations = useMemo(
    () => (response?.items ?? []) as unknown as Recommendation[],
    [response?.items],
  );

  const recommendations = useMemo(() => {
    const allSelfServiceableSelected =
      searchParams.self_serviceable.length >=
      ALL_SELF_SERVICEABLE_VALUES.length;
    const allKindSelected = selectedKinds.length >= ALL_KIND_VALUES.length;

    let filtered = allRecommendations;

    if (!allSelfServiceableSelected || !allKindSelected) {
      filtered = allRecommendations.filter((r) => {
        const selfServ =
          typeof r.self_serviceable === "string"
            ? r.self_serviceable
            : r.self_serviceable === true
              ? "deflectable"
              : null;
        const itemKind = (r as Record<string, unknown>).kind as
          | string
          | undefined;

        const matchesSelfServiceable =
          selfServ && searchParams.self_serviceable.includes(selfServ);
        const matchesKind = itemKind && selectedKinds.includes(itemKind);

        return matchesSelfServiceable || matchesKind;
      });
    }

    // The server enforces the quality scope too (high_quality query param);
    // this stays as a fallback for a UI deployed ahead of insights-engine.
    if (qualityFilter !== undefined) {
      filtered = filtered.filter((r) => {
        const convCount = r.conversation_count ?? r.evidence.conversation_count;
        const verdict = (r.evaluation as Record<string, unknown> | null)
          ?.verdict as string | undefined;
        const itemKind = (r as Record<string, unknown>).kind as
          | string
          | undefined;
        const passes =
          convCount >= 10 ||
          verdict === "high_value" ||
          (verdict === "useful" && convCount >= 6) ||
          itemKind === "deflectable";
        return passes === qualityFilter;
      });
    }

    return filtered;
  }, [
    allRecommendations,
    qualityFilter,
    searchParams.self_serviceable,
    selectedKinds,
  ]);

  const totalCount = recommendations.length;
  const pageCount = Math.ceil(totalCount / searchParams.pageSize);

  // The API caps a page at MAX_PAGE_SIZE while filtering and pagination happen
  // client-side, so anything beyond that is unreachable and must be surfaced.
  const serverTotal = response?.total ?? 0;
  const receivedCount = allRecommendations.length;
  const isTruncated = serverTotal > receivedCount;

  const breakdownReceived = allForTopFindings?.items.length ?? 0;
  const breakdownTotal = allForTopFindings?.total ?? 0;
  const isBreakdownTruncated = breakdownTotal > breakdownReceived;

  const paginatedRecommendations = useMemo(() => {
    const start = searchParams.page * searchParams.pageSize;
    return recommendations.slice(start, start + searchParams.pageSize);
  }, [recommendations, searchParams.page, searchParams.pageSize]);

  const classificationCounts = useMemo(() => {
    const items = (allForTopFindings?.items ??
      []) as unknown as Recommendation[];
    const selfServiceableCounts = new Map<string, number>();
    const kindCounts = new Map<string, number>();
    for (const item of items) {
      const val = item.self_serviceable;
      if (typeof val === "string") {
        selfServiceableCounts.set(
          val,
          (selfServiceableCounts.get(val) ?? 0) + 1,
        );
      } else if (val === true) {
        selfServiceableCounts.set(
          "deflectable",
          (selfServiceableCounts.get("deflectable") ?? 0) + 1,
        );
      }
      const itemKind = (item as Record<string, unknown>).kind as
        | string
        | undefined;
      if (itemKind) {
        kindCounts.set(itemKind, (kindCounts.get(itemKind) ?? 0) + 1);
      }
    }
    return { selfServiceable: selfServiceableCounts, kind: kindCounts };
  }, [allForTopFindings]);

  const deflectableCount = useMemo(
    () =>
      DEFLECTABLE_OPTIONS.reduce(
        (sum, o) =>
          sum + (classificationCounts.selfServiceable.get(o.value) ?? 0),
        0,
      ),
    [classificationCounts],
  );

  const automatableCount = useMemo(
    () =>
      AUTOMATABLE_OPTIONS.reduce(
        (sum, o) => sum + (classificationCounts.kind.get(o.value) ?? 0),
        0,
      ),
    [classificationCounts],
  );

  const structuredEscalationCount = useMemo(
    () => classificationCounts.kind.get("structured_escalation") ?? 0,
    [classificationCounts],
  );

  const kbKindCount = useMemo(
    () => classificationCounts.kind.get("deflectable") ?? 0,
    [classificationCounts],
  );

  const topFindings = useMemo(() => {
    const items = (allForTopFindings?.items ??
      []) as unknown as Recommendation[];
    if (items.length === 0) return [];

    return items
      .filter((r) => r.status === "DRAFT" || r.status === "DRIFTED")
      .map((r) => {
        const classScore = getClassificationScore(r.self_serviceable);
        const sourceCount =
          r.conversation_count ?? r.evidence.conversation_count;
        const score = classScore * 10 + Math.min(sourceCount, 20);
        return { rec: r, score, sourceCount };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [allForTopFindings]);

  const columns = useMemo(() => createReadOnlyColumns(), []);

  const sortingState: SortingState = [
    { id: searchParams.sortBy, desc: searchParams.sortDirection === "desc" },
  ];
  const paginationState: PaginationState = {
    pageSize: searchParams.pageSize,
    pageIndex: searchParams.page,
  };
  const columnFiltersState: ColumnFiltersState = [];

  const updateSearch = (updates: Partial<SearchSchema>) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, ...updates }),
    });
  };

  const onSortingChange: OnChangeFn<SortingState> = (value) => {
    const next = typeof value === "function" ? value(sortingState) : value;
    if (next.length > 0) {
      updateSearch({
        sortBy: next[0].id,
        sortDirection: next[0].desc ? "desc" : "asc",
        page: 0,
      });
    }
  };

  const onPaginationChange: OnChangeFn<PaginationState> = (value) => {
    const next = typeof value === "function" ? value(paginationState) : value;
    updateSearch({ page: next.pageIndex, pageSize: next.pageSize });
  };

  const onGlobalFilterChange: OnChangeFn<string> = (value) => {
    const newValue =
      typeof value === "function" ? value(searchParams.q) : value;
    updateSearch({ q: newValue, page: 0 });
  };

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = () => {
    // Filters are explicit controls above the table
  };

  const table = useDataTable<Recommendation>({
    name: "kb-insights",
    columns,
    data: paginatedRecommendations,
    pageCount,
    rowCount: totalCount,
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
    state: {
      sorting: sortingState,
      pagination: paginationState,
      columnFilters: columnFiltersState,
      globalFilter: searchParams.q,
    },
    onSortingChange,
    onPaginationChange,
    onColumnFiltersChange,
    onGlobalFilterChange,
    enableRowSelection: false,
  });

  const toggleSelfServiceable = (value: string) => {
    const current = searchParams.self_serviceable;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateSearch({ self_serviceable: next, page: 0 });
  };

  const toggleKind = (value: string) => {
    const current = searchParams.kind;
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateSearch({ kind: next, page: 0 });
  };

  const isStatusActive = (value: string) =>
    searchParams.status.length === 1 && searchParams.status[0] === value;

  const setStatusFilter = (value: string | null) => {
    updateSearch({ status: value ? [value] : [], page: 0 });
  };

  return (
    <Page>
      <PageHeader>
        <div className="flex flex-col gap-1">
          <PageTitle>Insights</PageTitle>
          <p className="text-muted-foreground text-sm">
            Inspect knowledge base recommendations and pipeline output
          </p>
        </div>
      </PageHeader>

      {/* Tenant selector */}
      <div className="@md:px-0 flex items-center gap-3 px-4">
        <CellSelect
          value={selectedTenant?.deployment_cell ?? cellFilter}
          onChange={handleCellChange}
        />
        <Label className="whitespace-nowrap text-sm font-medium">Tenant:</Label>
        <Popover open={tenantOpen} onOpenChange={setTenantOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={tenantOpen}
              className="w-[280px] justify-between"
            >
              {selectedTenant
                ? tenantLabel(selectedTenant)
                : "Select a tenant..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search tenants..." />
              <CommandList className="max-h-[300px]">
                <CommandEmpty>
                  {scope.tenantsLoading
                    ? "Loading tenants..."
                    : "No tenant found."}
                </CommandEmpty>
                <CommandGroup>
                  {sortedTenants.map((t) => (
                    <CommandItem
                      key={t.id}
                      value={`${t.company} ${t.id}`}
                      onSelect={() => {
                        updateSearch({
                          tenant_id: t.id,
                          page: 0,
                        });
                        setTenantOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          searchParams.tenant_id === t.id
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="flex-1 truncate">{tenantLabel(t)}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {!hasTenant ? (
        <TopTenantsList
          scope={scope}
          onSelectTenant={(id) => {
            updateSearch({ tenant_id: id, page: 0 });
          }}
        />
      ) : (
        <div className="@md:px-0 flex flex-col gap-4 px-4">
          {scope.showCells && (
            <CellScopeAlerts
              scope={scope}
              failures={
                recommendationsError && tenantCell
                  ? [{ cell: tenantCell, error: recommendationsError }]
                  : []
              }
              loadedCells={
                tenantCell && !recommendationsError ? [tenantCell] : []
              }
              isLoading={isLoading}
            />
          )}
          {/* Summary cards */}
          <div className="@sm:grid-cols-3 grid grid-cols-1 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Recommendations
                </CardTitle>
                <MessageSquare className="text-muted-foreground h-4 w-4" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="bg-muted h-8 w-20 animate-pulse rounded" />
                ) : (
                  <div className="text-2xl font-bold">{totalCount}</div>
                )}
                {isTruncated && (
                  <p className="mt-1 text-[11px] text-amber-600">
                    Truncated: showing {receivedCount} of {serverTotal} matching
                    recommendations (API returns at most {MAX_PAGE_SIZE} per
                    request). Narrow the filters to reach the rest.
                  </p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Actionable
                </CardTitle>
                <TrendingUp className="text-muted-foreground h-4 w-4" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="bg-muted h-8 w-20 animate-pulse rounded" />
                ) : (
                  <div className="text-2xl font-bold text-green-600">
                    {topFindings.length}
                  </div>
                )}
                <p className="text-muted-foreground text-xs">Top findings</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Classification
                </CardTitle>
                <Sparkles className="text-muted-foreground h-4 w-4" />
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="bg-muted h-8 w-20 animate-pulse rounded" />
                ) : (
                  <ClassificationBreakdown
                    items={
                      (allForTopFindings?.items ??
                        []) as unknown as Recommendation[]
                    }
                  />
                )}
                {isBreakdownTruncated && (
                  <p className="mt-1.5 text-[11px] text-amber-600">
                    Counted from the first {breakdownReceived} of{" "}
                    {breakdownTotal} recommendations
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Findings */}
          {topFindings.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  <CardTitle>Top Findings</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {topFindings.map(({ rec, sourceCount }, idx) => {
                  const content = rec.content as unknown as Record<
                    string,
                    unknown
                  >;
                  const title = (content.title ?? content.name ?? "") as string;
                  return (
                    <Link
                      key={rec.id}
                      to="/agent/kb-insights/$recId"
                      params={{ recId: rec.id }}
                      search={{ tenant_id: searchParams.tenant_id }}
                      className={cn(
                        "hover:bg-accent/50 flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
                      )}
                    >
                      <span className="bg-primary/10 text-primary flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{title}</p>
                        {rec.representative_intent &&
                          rec.representative_intent !== title && (
                            <p className="text-muted-foreground mt-0.5 truncate text-xs">
                              {rec.representative_intent}
                            </p>
                          )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <ClassificationBadge value={rec.self_serviceable} />
                        <Badge
                          variant="secondary"
                          className="text-[10px] tabular-nums"
                        >
                          {sourceCount} sources
                        </Badge>
                      </div>
                    </Link>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Filter bar */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* Quality scope toggles: at least one stays on */}
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  aria-pressed={showHigh}
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    showHigh &&
                      "!border-emerald-300 !bg-emerald-50 font-medium !text-emerald-700",
                  )}
                  onClick={() => {
                    if (showHigh && !showBelow) return;
                    updateSearch({ highQuality: !showHigh, page: 0 });
                  }}
                >
                  High Quality
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-pressed={showBelow}
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    showBelow &&
                      "!border-amber-300 !bg-amber-50 font-medium !text-amber-700",
                  )}
                  onClick={() => {
                    if (showBelow && !showHigh) return;
                    updateSearch({
                      highQuality: showHigh,
                      belowThreshold: !showBelow,
                      page: 0,
                    });
                  }}
                >
                  Below Threshold
                </Button>
              </div>

              <div className="bg-border h-4 w-px" />

              {/* Deflectable filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-7 gap-1 px-2.5 text-xs",
                      searchParams.self_serviceable.some((v) =>
                        DEFLECTABLE_OPTIONS.some((o) => o.value === v),
                      ) &&
                        "!border-green-300 !bg-green-50 font-medium !text-green-700",
                    )}
                  >
                    Deflectable
                    <Badge className="ml-1 h-4 bg-green-600 px-1 text-[9px] text-white">
                      {deflectableCount}
                    </Badge>
                    <ChevronDown className="size-3 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-2" align="start">
                  <div className="space-y-1">
                    {SELF_SERVICEABLE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                      >
                        <Checkbox
                          checked={searchParams.self_serviceable.includes(
                            opt.value,
                          )}
                          onCheckedChange={() => {
                            toggleSelfServiceable(opt.value);
                          }}
                        />
                        <span className="flex flex-1 items-center gap-1">
                          {opt.label}
                          {CLASSIFICATION_TOOLTIPS.get(opt.value) && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger
                                  asChild
                                  onClick={(e) => {
                                    e.preventDefault();
                                  }}
                                >
                                  <Info className="text-muted-foreground size-3" />
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  className="max-w-64"
                                >
                                  {CLASSIFICATION_TOOLTIPS.get(opt.value)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                          (
                          {classificationCounts.selfServiceable.get(
                            opt.value,
                          ) ?? 0}
                          )
                        </span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Automatable filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-7 gap-1 px-2.5 text-xs",
                      searchParams.kind.some((v) =>
                        AUTOMATABLE_OPTIONS.some((o) => o.value === v),
                      ) &&
                        "!border-blue-300 !bg-blue-50 font-medium !text-blue-700",
                    )}
                  >
                    Automatable
                    <Badge className="ml-1 h-4 bg-blue-600 px-1 text-[9px] text-white">
                      {automatableCount}
                    </Badge>
                    <ChevronDown className="size-3 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  <div className="space-y-1">
                    {AUTOMATABLE_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                      >
                        <Checkbox
                          checked={searchParams.kind.includes(opt.value)}
                          onCheckedChange={() => {
                            toggleKind(opt.value);
                          }}
                        />
                        <span className="flex-1">{opt.label}</span>
                        <span className="text-muted-foreground text-xs tabular-nums">
                          ({classificationCounts.kind.get(opt.value) ?? 0})
                        </span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* KB article (kind=deflectable) filter */}
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 gap-1 px-2.5 text-xs",
                  searchParams.kind.includes("deflectable") &&
                    "!border-teal-300 !bg-teal-50 font-medium !text-teal-700",
                )}
                onClick={() => {
                  toggleKind("deflectable");
                }}
              >
                KB Article
                <Badge className="ml-1 h-4 bg-teal-600 px-1 text-[9px] text-white">
                  {kbKindCount}
                </Badge>
              </Button>

              {/* Structured Escalation filter */}
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 gap-1 px-2.5 text-xs",
                  searchParams.kind.includes("structured_escalation") &&
                    "!border-amber-300 !bg-amber-50 font-medium !text-amber-700",
                )}
                onClick={() => {
                  toggleKind("structured_escalation");
                }}
              >
                Structured Escalation
                <Badge className="ml-1 h-4 bg-amber-600 px-1 text-[9px] text-white">
                  {structuredEscalationCount}
                </Badge>
              </Button>

              <div className="bg-border h-4 w-px" />

              {/* Status filters */}
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    searchParams.status.length === 0 &&
                      "!bg-primary/15 !border-primary !text-foreground font-medium",
                  )}
                  onClick={() => {
                    setStatusFilter(null);
                  }}
                >
                  All
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    isStatusActive("APPROVED") &&
                      "!bg-primary/15 !border-primary !text-foreground font-medium",
                  )}
                  onClick={() => {
                    setStatusFilter(
                      isStatusActive("APPROVED") ? null : "APPROVED",
                    );
                  }}
                >
                  Submitted
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 px-2.5 text-xs",
                    isStatusActive("DISMISSED") &&
                      "!bg-primary/15 !border-primary !text-foreground font-medium",
                  )}
                  onClick={() => {
                    setStatusFilter(
                      isStatusActive("DISMISSED") ? null : "DISMISSED",
                    );
                  }}
                >
                  Dismissed
                </Button>
              </div>

              <div className="bg-border h-4 w-px" />

              {/* Sources slider */}
              <div className="flex items-center gap-2">
                <Label className="text-muted-foreground whitespace-nowrap text-xs">
                  Sources:
                </Label>
                <span className="text-muted-foreground w-8 text-right text-xs tabular-nums">
                  {searchParams.minSources === 0
                    ? "Any"
                    : searchParams.minSources}
                </span>
                <Slider
                  min={0}
                  max={21}
                  step={1}
                  value={[searchParams.minSources, searchParams.maxSources]}
                  onValueChange={(value) => {
                    updateSearch({
                      minSources: value[0],
                      maxSources: value[1],
                      page: 0,
                    });
                  }}
                  className="w-32"
                />
                <span className="text-muted-foreground w-8 text-xs tabular-nums">
                  {searchParams.maxSources >= 21
                    ? "20+"
                    : searchParams.maxSources}
                </span>
                {(searchParams.minSources !== 0 ||
                  searchParams.maxSources < 21) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      updateSearch({ minSources: 0, maxSources: 21, page: 0 });
                    }}
                    className="h-6 px-1.5 text-[10px]"
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <DataTableToolbar
            ref={toolbarRef}
            table={table}
            onRefresh={() => {
              void refetch();
            }}
            isLoading={isRefetching}
          />

          {/* Empty state for a quality-scoped view */}
          {qualityFilter !== undefined &&
            !isLoading &&
            !isRefetching &&
            totalCount === 0 &&
            serverTotal === 0 && (
              <Card>
                <CardContent className="flex flex-col items-center gap-2 py-8 text-center">
                  <p className="text-sm font-medium">
                    {qualityFilter
                      ? "No high quality articles found for this tenant."
                      : "No articles below the quality threshold for this tenant."}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {qualityFilter
                      ? "Articles that did not pass the quality filter are available by turning on Below Threshold."
                      : "Articles that passed the quality filter are available by turning on High Quality."}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      updateSearch({
                        highQuality: true,
                        belowThreshold: true,
                        page: 0,
                      });
                    }}
                  >
                    Show all articles
                  </Button>
                </CardContent>
              </Card>
            )}

          {/* Table */}
          <DataTable
            table={table}
            totalCount={totalCount}
            className="w-full"
            scope="kb-insights"
            data={paginatedRecommendations}
            getRowUrl={(row) =>
              `/agent/kb-insights/${row.id}?tenant_id=${searchParams.tenant_id}`
            }
            isLoading={isLoading || isRefetching}
            toolbarRef={toolbarRef}
          />
        </div>
      )}
    </Page>
  );
}

function ClassificationBadge({ value }: { value: string | boolean | null }) {
  if (value === true || value === "deflectable") {
    return (
      <Badge
        variant="outline"
        className="border-green-200 text-[10px] text-green-600"
      >
        Deflectable
      </Badge>
    );
  }
  if (value === "potentially_deflectable") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="border-emerald-200 text-[10px] text-emerald-500"
            >
              Potentially
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64">
            No evidence it would lead to deflections, but there is evidence the
            article arose from conversation deflection attempts
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (value === "partially_deflectable") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="border-yellow-200 text-[10px] text-yellow-600"
            >
              Partially
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-64">
            In some cases there were opportunities for deflection, in others
            there weren't
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  if (value === "not_deflectable") {
    return (
      <Badge
        variant="outline"
        className="text-muted-foreground border-border text-[10px]"
      >
        Not Deflectable
      </Badge>
    );
  }
  if (value === "automatable") {
    return (
      <Badge
        variant="outline"
        className="border-blue-200 text-[10px] text-blue-600"
      >
        Automatable
      </Badge>
    );
  }
  if (value === "automatable_with_approval") {
    return (
      <Badge
        variant="outline"
        className="border-violet-200 text-[10px] text-violet-600"
      >
        Auto+Approval
      </Badge>
    );
  }
  if (value === "structured_escalation") {
    return (
      <Badge
        variant="outline"
        className="border-amber-200 text-[10px] text-amber-600"
      >
        Escalation
      </Badge>
    );
  }
  return null;
}

function ClassificationBreakdown({ items }: { items: Recommendation[] }) {
  const entries = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of items) {
      const val = item.self_serviceable;
      if (typeof val === "string" && val !== "junk") {
        map.set(val, (map.get(val) ?? 0) + 1);
      } else if (val === true) {
        map.set("deflectable", (map.get("deflectable") ?? 0) + 1);
      }
    }
    return Array.from(map.entries()).sort(
      (a, b) =>
        (CLASSIFICATION_RANK.get(b[0]) ?? 0) -
        (CLASSIFICATION_RANK.get(a[0]) ?? 0),
    );
  }, [items]);

  if (entries.length === 0) {
    return <span className="text-muted-foreground text-sm">No data</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {entries.map(([key, count]) => (
        <Badge key={key} variant="outline" className="text-[10px]">
          {formatClassLabel(key)}: {count}
        </Badge>
      ))}
    </div>
  );
}

function formatClassLabel(value: string): string {
  switch (value) {
    case "deflectable":
      return "Deflect";
    case "potentially_deflectable":
      return "Potential";
    case "partially_deflectable":
      return "Partial";
    case "not_deflectable":
      return "Not Deflect";
    case "automatable":
      return "Auto";
    case "automatable_with_approval":
      return "Auto+Appr";
    case "structured_escalation":
      return "Escalation";
    default:
      return value;
  }
}

function TopTenantsList({
  scope,
  onSelectTenant,
}: {
  scope: CellScope;
  onSelectTenant: (id: string) => void;
}) {
  const tenants = scope.tenants;
  const {
    counts,
    failures,
    loadingCells,
    isLoading: countsLoading,
  } = useCellRecommendationCountsByTenant(scope.queriedCells, tenants);
  const isLoading = countsLoading || scope.queriedCells.length === 0;

  const topTenants = useMemo(() => {
    if (!tenants?.length) return [];
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([tenantId, count]) => {
        const tenant = tenants.find((t) => t.id === tenantId);
        return {
          id: tenantId,
          name: tenant ? tenantLabel(tenant) : tenantId.slice(0, 8),
          count,
        };
      });
  }, [counts, tenants]);

  const loadedCells = scope.queriedCells.filter(
    (cell) =>
      !failures.some((failure) => failure.cell.id === cell.id) &&
      !loadingCells.some((loading) => loading.id === cell.id),
  );
  const alerts = scope.showCells && (
    <div className="@md:px-0 mb-3 px-4">
      <CellScopeAlerts
        scope={scope}
        failures={failures}
        loadedCells={loadedCells}
        isLoading={isLoading}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="@md:px-0 px-4 py-8">
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            // eslint-disable-next-line react-x/no-array-index-key
            <div key={i} className="bg-muted h-12 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (topTenants.length === 0) {
    return (
      <>
        {alerts}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20">
          <BookOpen className="text-muted-foreground size-10" />
          <p className="text-muted-foreground text-sm">
            No insights data available yet
          </p>
        </div>
      </>
    );
  }

  const maxCount = topTenants[0]?.count ?? 1;

  return (
    <div className="@md:px-0 px-4">
      {alerts}
      <h3 className="text-muted-foreground mb-3 text-sm font-medium">
        Top tenants by insights opportunities
      </h3>
      <div className="space-y-1.5">
        {topTenants.map((t, i) => (
          <button
            type="button"
            key={t.id}
            onClick={() => {
              onSelectTenant(t.id);
            }}
            className="hover:bg-accent flex w-full items-center gap-3 rounded-lg border px-4 py-2.5 text-left transition-colors"
          >
            <span className="text-muted-foreground w-5 shrink-0 font-mono text-xs">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {t.name}
            </span>
            <div className="flex shrink-0 items-center gap-2">
              <div className="bg-muted h-1.5 w-24 overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full rounded-full"
                  style={{ width: `${(t.count / maxCount) * 100}%` }}
                />
              </div>
              <span className="text-muted-foreground w-8 text-right text-xs tabular-nums">
                {t.count}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
